import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type LeadviewTab = "Grid" | "List" | "Board";

export interface LeadViewTab {
  label: LeadviewTab;
  value: string;
}

export const LeadViewTabs: LeadViewTab[] = [
  {
    label: "Grid",
    value: "Grid",
  },
  {
    label: "List",
    value: "List",
  },
  {
    label: "Board",
    value: "Board",
  },
];

/**
 * An UNSAVED Stage/status pick in the drawer's Stage section.
 *
 * Published by `StageSection` so the drawer's FOLLOW-UP picker
 * (`ActionForNextFollowup`, a different subtree) can answer the two questions
 * it now has to answer about a status the lead has not moved into yet:
 *   1. what the follow-up dial of THAT bucket says (default due-in days and
 *      the "Maximum days allowed" horizon), and
 *   2. whether creating the follow-up should also apply the move
 *      ("Update status & follow-up").
 *
 * Only published when all three ids are set — exactly what
 * `StageBypassConfirmation` requires of the Update button, so the two ways to
 * update a status can never send different payloads. `entityKey` pins it to
 * the lead/deal it was picked on; a draft from another row is ignored, never
 * applied. Not persisted (see `leadFunctionalPersistConfig`) and cleared when
 * the Stage section unmounts.
 */
export interface PendingStageDraft {
  /** `lead:123` / `child:456` — the row this draft was picked on. */
  entityKey: string;
  stageId: number;
  statusId: number;
  subStatusId: number;
  stageName: string;
  statusName: string;
  subStatusName: string;
  /** True when the draft leaves the current STAGE (not just its status). */
  isStageMove: boolean;
}

const initialState = {
  isAddLead: false,
  isAssign: false,
  // Peer-transfer drawer (hand a lead/deal you hold to a same-role peer).
  // Separate from `isAssign` so the simplified peer sheet and the full
  // assign sheet never fight over the same flag.
  isPeerTransfer: false,
  currentTab: "Overview",
  currentViewTab: "Grid",
  currentLeadId: null,
  selectedApplicationId: null,
  isEditMode: false,
  isStageBypassConfirmation: false,
  pendingStageDraft: null as PendingStageDraft | null,
  isFollowUpOverrideConfirmation: false,
  isAssignMode: false,
  isAssignModalOpen: false,
  isUpdateMode: false,
  isUpdateModalOpen: false,
  selectedLeadIds: [] as (string | number)[],
  selectedUser: "",
  selectedBranch: "" as string | number,
  isAssignConfirmationModalOpen: false,
  isUpdationConfirmationModalOpen: false,
  /**
   * Tied to the "Archive old activity logs for current assignee" checkbox
   * in the Assign Lead drawer. When true, the assign / branch-transfer
   * mutations include `archiveOldHistory: true`, telling the server to
   * flip every pre-existing activity log / note / follow-up / deadline
   * for those leads to `isArchived = true`. Resets when the drawer closes
   * so a stale tick doesn't leak into the next assignment.
   */
  archiveOldHistory: false,
  isArchivedLeads: false,
  // "Search all leads" modal (org-gated tenant-wide lookup). Not one of the
  // mutually-exclusive PANEL flags below — it opens OVER the current list.
  isGlobalSearchOpen: false,
  isDuplicateLeads: false,
  isBranchMismatchLeads: false,
  isDuplicateRolesLeads: false,
  isFilterOpen: false,
  /**
   * The List view is open FULL SCREEN (see ListFullscreenShell) — one global
   * flag for the leads page, stage routes and favorite views alike. Not
   * persisted (the whitelist is `currentViewTab` only) and cleared by the
   * body that renders the overlay when it unmounts.
   */
  isListFullscreen: false,
  /**
   * The ACTIVE anchor's stage id when the list the user is on has an anchor
   * ("Verified") filter active, else null. Synced by LeadsPage (from
   * leadFilterSlice.verifiedAnchor) and Dynamics (from its per-route slice), so
   * the shared lead view knows to show only the one deal verified for THAT
   * anchor, regardless of which page opened it.
   */
  verifiedDealView: null as number | null,
  paymentsDealIdToOpen: null as string | null,
  /**
   * Which INSTALMENT the Payments tab should preselect once it opens, set
   * alongside `paymentsDealIdToOpen` by the "Record payment" action on the
   * instalment-due popup.
   *
   * An additive sibling field rather than widening `paymentsDealIdToOpen` into
   * an object: the two existing writers (the Payments tab's own deep-link
   * effect and the academy child panel) keep working untouched, and
   * `leadFunctional`'s persist whitelist is `["currentViewTab"]` so there is no
   * rehydration shape to migrate.
   */
  paymentsInstallmentIdToOpen: null as string | null,
  isImportLeadOpen: false,
  /** "Select From Excel" modal — match uploaded/sheet phone numbers to
   *  existing leads and select them for a bulk action. */
  isSelectFromExcelOpen: false,
  bulkUpdate: {
    stageId: "" as string,
    statusId: "" as string,
    subStatusId: "" as string,
    priority: "" as string,
    leadSourceId: "" as string,
    campaignId: "" as string,
    /** ISO date string for the follow-up to apply to every selected lead. */
    followUpDate: "" as string,
    followUpNote: "" as string,
    /** ISO date string for the deadline to apply to every selected lead. */
    deadlineDate: "" as string,
    deadlineNote: "" as string,
    /**
     * Lead-level selector custom fields. Keyed by customInputId (stringified
     * because Redux Toolkit prefers serialisable keys). Value is either an
     * option id ("12") or the literal "none" to clear the field. Empty
     * string / missing key = leave the field untouched on the lead.
     */
    customFields: {} as Record<string, string>,
    /**
     * Tracks section. Keyed by trackId (stringified, like customFields).
     * Value is a choice (SubTrack) id ("21") or the literal "none" = take the
     * selected leads off that track. Missing key = leave the track alone.
     */
    trackUpdates: {} as Record<string, string>,
    /**
     * Precise deal targeting for the stage move. Stage ids (stringified) whose
     * DEALS should be moved. Empty = plain lead-level bulk move (every selected
     * lead). Non-empty = only leads that have a deal currently in one of these
     * stages are affected — the matching deal(s) move; the rest are skipped.
     * `sourceStatusIds` / `sourceSubStatusIds` optionally narrow the match to
     * specific status / sub-status (AND across dimensions).
     */
    sourceStageIds: [] as string[],
    sourceStatusIds: [] as string[],
    sourceSubStatusIds: [] as string[],
    /**
     * Precise mode only: move JUST the matching deal(s) and leave the lead row
     * untouched (operator "Change only deal" flow). Ignored when no source is
     * picked.
     */
    preciseDealOnly: false as boolean,
    /**
     * Admin-scoped FORCE for the stage move: skip the target stage's
     * requirement checklist. Only sent when `selectCanForceStageUpdate`
     * allows it; the server re-checks.
     */
    force: false as boolean,
    /**
     * Bulk "Assign to": the user every selected lead is handed to (lead level,
     * same write as the Assign drawer — same eligibility / same-role revoke
     * preflight). Empty = no assignment in this bulk. `assignUserName` is
     * display-only (preview row). `assignFresh` = "Give it as a fresh lead".
     */
    assignUserId: "" as string,
    assignUserName: "" as string,
    assignFresh: false as boolean,
    /**
     * Bulk "Assign to" → Assign Branch: the branch every selected lead is
     * transferred to (same write as the Assign drawer's branch tab). A bulk
     * carries a user OR a branch, never both — picking one clears the other.
     */
    assignBranchId: "" as string,
    assignBranchName: "" as string,
  },
};

const leadFunctionalSlice = createSlice({
  name: "leadFunctional",
  initialState,
  reducers: {
    setIsFilterOpen(state, action) {
      state.isFilterOpen = action.payload;
    },
    setIsListFullscreen(state, action: PayloadAction<boolean>) {
      state.isListFullscreen = action.payload;
    },
    setVerifiedDealView(state, action) {
      state.verifiedDealView = action.payload;
    },
    setIsAssign: (state, action) => {
      state.isAssign = action.payload;
    },
    setIsPeerTransfer: (state, action: PayloadAction<boolean>) => {
      state.isPeerTransfer = action.payload;
    },
    setIsAddLead: (state, action: PayloadAction<boolean>) => {
      state.isAddLead = action.payload;
      state.currentLeadId = null;
    },
    setLeadCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
    setCurrentLeadId: (state, action) => {
      state.currentLeadId = action.payload;
    },
    setLeadCurrentViewTab: (state, action) => {
      state.currentViewTab = action.payload;
    },
    setSelectedApplicationId: (state, action) => {
      state.selectedApplicationId = action.payload;
    },
    setIsEditMode: (state, action) => {
      state.isEditMode = action.payload;
    },
    setIsStageBypassConfirmation: (state, action: PayloadAction<boolean>) => {
      state.isStageBypassConfirmation = action.payload;
    },
    /** @see PendingStageDraft — `null` clears it (saved, reverted, unmounted). */
    setPendingStageDraft: (
      state,
      action: PayloadAction<PendingStageDraft | null>,
    ) => {
      state.pendingStageDraft = action.payload;
    },
    setIsFollowUpOverrideConfirmation: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isFollowUpOverrideConfirmation = action.payload;
    },
    setIsAssignMode: (state, action: PayloadAction<boolean>) => {
      state.isAssignMode = action.payload;
    },
    setIsAssignModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAssignModalOpen = action.payload;
      // Closing the drawer always clears the archive intent — a fresh
      // open should start unchecked so the user has to opt in each time.
      if (!action.payload) state.archiveOldHistory = false;
    },
    setArchiveOldHistory: (state, action: PayloadAction<boolean>) => {
      state.archiveOldHistory = action.payload;
    },
    setIsUpdateMode: (state, action: PayloadAction<boolean>) => {
      state.isUpdateMode = action.payload;
    },
    setIsUpdateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isUpdateModalOpen = action.payload;
    },
    setSelectedLeadIds: (state, action: PayloadAction<(string | number)[]>) => {
      state.selectedLeadIds = action.payload;
    },
    toggleLeadSelection: (state, action: PayloadAction<string | number>) => {
      const id = action.payload;
      if (state.selectedLeadIds.includes(id)) {
        state.selectedLeadIds = state.selectedLeadIds.filter(
          (item) => item !== id
        );
      } else {
        state.selectedLeadIds.push(id);
      }
    },
    clearSelectedLeadIds: (state) => {
      state.selectedLeadIds = [];
    },
    selectAllLeads: (state, action: PayloadAction<(string | number)[]>) => {
      state.selectedLeadIds = action.payload;
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    setSelectedBranch: (state, action: PayloadAction<string | number>) => {
      state.selectedBranch = action.payload;
    },
    setIsAssignConfirmationModalOpen: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isAssignConfirmationModalOpen = action.payload;
    },
    setIsUpdationConfirmationModalOpen: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isUpdationConfirmationModalOpen = action.payload;
    },
    setIsGlobalSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.isGlobalSearchOpen = action.payload;
    },
    setIsArchivedLeads: (state, action: PayloadAction<boolean>) => {
      state.isArchivedLeads = action.payload;
      if (action.payload) {
        state.isDuplicateLeads = false;
        state.isBranchMismatchLeads = false;
        state.isDuplicateRolesLeads = false;
      }
    },
    setIsDuplicateLeads: (state, action: PayloadAction<boolean>) => {
      state.isDuplicateLeads = action.payload;
      if (action.payload) {
        state.isArchivedLeads = false;
        state.isBranchMismatchLeads = false;
        state.isDuplicateRolesLeads = false;
      }
    },
    setIsBranchMismatchLeads: (state, action: PayloadAction<boolean>) => {
      state.isBranchMismatchLeads = action.payload;
      if (action.payload) {
        state.isArchivedLeads = false;
        state.isDuplicateLeads = false;
        state.isDuplicateRolesLeads = false;
      }
    },
    setIsDuplicateRolesLeads: (state, action: PayloadAction<boolean>) => {
      state.isDuplicateRolesLeads = action.payload;
      if (action.payload) {
        state.isArchivedLeads = false;
        state.isDuplicateLeads = false;
        state.isBranchMismatchLeads = false;
      }
    },
    setPaymentsDealIdToOpen: (state, action: PayloadAction<string | null>) => {
      state.paymentsDealIdToOpen = action.payload;
    },
    setPaymentsInstallmentIdToOpen: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.paymentsInstallmentIdToOpen = action.payload;
    },
    setIsImportLeadOpen: (state, action: PayloadAction<boolean>) => {
      state.isImportLeadOpen = action.payload;
    },
    setIsSelectFromExcelOpen: (state, action: PayloadAction<boolean>) => {
      state.isSelectFromExcelOpen = action.payload;
    },
    setBulkUpdateStageId: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.stageId = action.payload;
      state.bulkUpdate.statusId = "";
      state.bulkUpdate.subStatusId = "";
    },
    setBulkUpdateStatusId: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.statusId = action.payload;
      state.bulkUpdate.subStatusId = "";
    },
    setBulkUpdateSubStatusId: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.subStatusId = action.payload;
    },
    setBulkUpdateSourceStageIds: (state, action: PayloadAction<string[]>) => {
      state.bulkUpdate.sourceStageIds = action.payload;
    },
    setBulkUpdateSourceStatusIds: (state, action: PayloadAction<string[]>) => {
      state.bulkUpdate.sourceStatusIds = action.payload;
    },
    setBulkUpdateSourceSubStatusIds: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      state.bulkUpdate.sourceSubStatusIds = action.payload;
    },
    setBulkUpdatePreciseDealOnly: (state, action: PayloadAction<boolean>) => {
      state.bulkUpdate.preciseDealOnly = action.payload;
    },
    setBulkUpdateForce: (state, action: PayloadAction<boolean>) => {
      state.bulkUpdate.force = action.payload;
    },
    setBulkUpdateAssignUser: (
      state,
      action: PayloadAction<{ id: string | number; name?: string } | null>,
    ) => {
      state.bulkUpdate.assignUserId =
        action.payload?.id != null ? String(action.payload.id) : "";
      state.bulkUpdate.assignUserName = action.payload?.name ?? "";
      if (action.payload) {
        state.bulkUpdate.assignBranchId = "";
        state.bulkUpdate.assignBranchName = "";
      } else {
        state.bulkUpdate.assignFresh = false;
      }
    },
    setBulkUpdateAssignBranch: (
      state,
      action: PayloadAction<{ id: string | number; name?: string } | null>,
    ) => {
      state.bulkUpdate.assignBranchId =
        action.payload?.id != null ? String(action.payload.id) : "";
      state.bulkUpdate.assignBranchName = action.payload?.name ?? "";
      if (action.payload) {
        state.bulkUpdate.assignUserId = "";
        state.bulkUpdate.assignUserName = "";
      } else {
        state.bulkUpdate.assignFresh = false;
      }
    },
    setBulkUpdateAssignFresh: (state, action: PayloadAction<boolean>) => {
      state.bulkUpdate.assignFresh = action.payload;
    },
    setBulkUpdatePriority: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.priority = action.payload;
    },
    setBulkUpdateLeadSourceId: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.leadSourceId = action.payload;
    },
    setBulkUpdateCampaignId: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.campaignId = action.payload;
    },
    setBulkUpdateFollowUpDate: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.followUpDate = action.payload;
    },
    setBulkUpdateFollowUpNote: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.followUpNote = action.payload;
    },
    setBulkUpdateDeadlineDate: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.deadlineDate = action.payload;
    },
    setBulkUpdateDeadlineNote: (state, action: PayloadAction<string>) => {
      state.bulkUpdate.deadlineNote = action.payload;
    },
    setBulkUpdateCustomField: (
      state,
      action: PayloadAction<{ customInputId: string; value: string }>,
    ) => {
      const { customInputId, value } = action.payload;
      if (!value) {
        delete state.bulkUpdate.customFields[customInputId];
      } else {
        state.bulkUpdate.customFields[customInputId] = value;
      }
    },
    clearBulkUpdateCustomFields: (state) => {
      state.bulkUpdate.customFields = {};
    },
    setBulkUpdateTrack: (
      state,
      action: PayloadAction<{ trackId: string; value: string }>,
    ) => {
      const { trackId, value } = action.payload;
      // Older persisted drafts have no trackUpdates yet.
      if (!state.bulkUpdate.trackUpdates) state.bulkUpdate.trackUpdates = {};
      if (!value) {
        delete state.bulkUpdate.trackUpdates[trackId];
      } else {
        state.bulkUpdate.trackUpdates[trackId] = value;
      }
    },
    resetBulkUpdate: (state) => {
      state.bulkUpdate = {
        stageId: "",
        statusId: "",
        subStatusId: "",
        priority: "",
        leadSourceId: "",
        campaignId: "",
        followUpDate: "",
        followUpNote: "",
        deadlineDate: "",
        deadlineNote: "",
        customFields: {},
        trackUpdates: {},
        sourceStageIds: [],
        sourceStatusIds: [],
        sourceSubStatusIds: [],
        preciseDealOnly: false,
        force: false,
        assignUserId: "",
        assignUserName: "",
        assignFresh: false,
        assignBranchId: "",
        assignBranchName: "",
      };
    },
  },
});

export const {
  setIsAddLead,
  setIsAssign,
  setIsPeerTransfer,
  setLeadCurrentTab,
  setCurrentLeadId,
  setLeadCurrentViewTab,
  setSelectedApplicationId,
  setIsEditMode,
  setIsStageBypassConfirmation,
  setPendingStageDraft,
  setIsFollowUpOverrideConfirmation,
  setIsAssignMode,
  setIsAssignModalOpen,
  setIsUpdateMode,
  setIsUpdateModalOpen,
  setSelectedLeadIds,
  toggleLeadSelection,
  clearSelectedLeadIds,
  selectAllLeads,
  setSelectedUser,
  setSelectedBranch,
  setIsAssignConfirmationModalOpen,
  setArchiveOldHistory,
  setIsUpdationConfirmationModalOpen,
  setIsArchivedLeads,
  setIsGlobalSearchOpen,
  setIsDuplicateLeads,
  setIsBranchMismatchLeads,
  setIsDuplicateRolesLeads,
  setIsFilterOpen,
  setIsListFullscreen,
  setVerifiedDealView,
  setPaymentsDealIdToOpen,
  setPaymentsInstallmentIdToOpen,
  setIsImportLeadOpen,
  setIsSelectFromExcelOpen,
  setBulkUpdateStageId,
  setBulkUpdateStatusId,
  setBulkUpdateSubStatusId,
  setBulkUpdateSourceStageIds,
  setBulkUpdateSourceStatusIds,
  setBulkUpdateSourceSubStatusIds,
  setBulkUpdatePreciseDealOnly,
  setBulkUpdateForce,
  setBulkUpdateAssignUser,
  setBulkUpdateAssignFresh,
  setBulkUpdateAssignBranch,
  setBulkUpdatePriority,
  setBulkUpdateLeadSourceId,
  setBulkUpdateCampaignId,
  setBulkUpdateFollowUpDate,
  setBulkUpdateFollowUpNote,
  setBulkUpdateDeadlineDate,
  setBulkUpdateDeadlineNote,
  setBulkUpdateCustomField,
  clearBulkUpdateCustomFields,
  setBulkUpdateTrack,
  resetBulkUpdate,
} = leadFunctionalSlice.actions;
export default leadFunctionalSlice.reducer;
